import { useMemo, useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ArticleCard from '@/components/feature/ArticleCard';
import { SITE } from '@/data/site';
import { categories } from '@/data/categories';
import { articles, searchArticles } from '@/data/articles';

const HOT_KEYWORDS = ['娱乐热点', '热梗', '综艺', '边界感', '信息辨别', '剧集'];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
  }, [query]);

  const results = useMemo(() => searchArticles(query), [query]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  return (
    <SiteLayout>
      <Seo
        title={query ? `搜索「${query}」的结果 - ${SITE.name}` : `站内搜索 - ${SITE.name}`}
        description={
          query
            ? `在${SITE.name}中搜索「${query}」的相关内容结果。`
            : `使用${SITE.name}站内搜索，按关键词查找娱乐热点、热剧综、网络热梗与瓜圈观察相关内容。`
        }
        keywords={`站内搜索,${SITE.name},${query || '娱乐内容'}`}
        path={query ? `/search?q=${encodeURIComponent(query)}` : '/search'}
      />

      <div className="max-w-container mx-auto px-4 md:px-8 pt-8">
        <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '站内搜索' }]} />
      </div>

      <section className="max-w-container mx-auto px-4 md:px-8 pt-8 pb-4">
        <div className="max-w-2xl">
          <h1 className="font-heading text-2xl md:text-3xl font-black text-foreground-950">
            站内搜索
          </h1>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            输入关键词，在全部 {articles.length} 篇文章的标题、摘要与标签中查找。
          </p>
          <form onSubmit={onSubmit} className="mt-5 flex flex-col sm:flex-row gap-3" role="search">
            <div className="flex-1 flex items-center gap-2 h-12 px-4 rounded-md border border-background-300 bg-background-50 focus-within:border-primary-400 transition-colors">
              <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="例如：热梗、综艺、信息辨别"
                aria-label="搜索关键词"
                className="flex-1 bg-transparent text-sm outline-none text-foreground-800 placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">热门关键词</span>
            {HOT_KEYWORDS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setInput(k);
                  setParams({ q: k });
                }}
                className="px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-container mx-auto px-4 md:px-8 pb-8">
        {query ? (
          <>
            <p className="text-sm text-foreground-500 mb-6 pb-4 border-b border-background-200">
              关键词「<span className="text-foreground-900 font-medium">{query}</span>」共找到{' '}
              <span className="text-primary-600 font-medium">{results.length}</span> 篇内容
            </p>
            {results.length > 0 ? (
              <div className="divide-y divide-background-200">
                {results.map((a) => (
                  <ArticleCard key={a.slug} article={a} variant="list" />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center">
                <span className="mx-auto w-14 h-14 rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
                  <i className="ri-search-eye-line text-2xl" />
                </span>
                <p className="mt-4 text-foreground-700">没有找到与「{query}」相关的内容</p>
                <p className="mt-2 text-sm text-foreground-500">
                  换个关键词试试，或者从下面的栏目里逛逛
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2">
                  {categories.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/category/${c.slug}`}
                      className="px-4 py-2 rounded-full border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                    >
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-5">
              不妨从这些内容开始
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.slice(0, 6).map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}